import { TextInput } from 'common-front/src/designSystem/components/textInput';
import { ToggleText } from 'common-front/src/designSystem/components/toggle';
import { RichSelect } from 'common-front/src/designSystem/form/richSelect';
import { TextInput as FormTextInput } from 'common-front/src/designSystem/form/textInput';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { useStateDebounce } from 'common-front/src/hooks/useStateDebounce';
import { useUsersSearch } from 'common-front/src/users/search/useUsersSearch';
import { Spacer } from 'common/src/designSystem/components/spacer';
import {
    Delegation,
    DelegationId,
    DelegationsCategory,
    UsersInfoId
} from 'common/src/generated/types';
import { isNonEmptyArray } from 'common/src/util/array';
import { Emptyable } from 'common/src/util/emptyable';
import * as React from 'react';
import { CreateDelegationCategory } from '../../delegationsCategories/create/createUpdateDelegationCategory';

interface ICreateDelegationInformationsProps {
    categories: Array<Pick<DelegationsCategory, 'id' | 'name'>>;
    delegationType: 'event' | 'organization';
    initialParentId: Emptyable<DelegationId>;
    leadersIds: UsersInfoId[];
    organizationDelegations: Array<Pick<Delegation, 'id' | 'name'>>;

    change(name: string, value: any): void;
}

const CreateDelegationInformations = (props: ICreateDelegationInformationsProps) => {
    const {
        translate,
        params: { organizationId, eventId }
    } = useHeavent();
    const [name, nameDebounced, setName] = useStateDebounce('');
    const { usersInfosOptions, isLoading } = useUsersSearch({
        organizationId,
        eventId,
        hasSelected: isNonEmptyArray(props.leadersIds),
        nameDebounced,
        userInfoIds: props.leadersIds
    });
    const [isCreateCategoryOpen, setIsCreateCategoryOpen] = React.useState(false);
    const [delegationsCategories, setDelegationsCategories] = React.useState(props.categories);
    const [isToggled, _setIsToggled] = React.useState(!!props.initialParentId);
    const setIsToggled = React.useCallback(
        (value: boolean) => {
            if (!value) {
                props.change('delegation.parentId', null);
            }

            _setIsToggled(value);
        },
        [props.change, _setIsToggled]
    );

    return (
        <>
            {props.delegationType === 'event' && isNonEmptyArray(props.organizationDelegations) && (
                <>
                    <ToggleText value={isToggled} onChange={setIsToggled}>
                        {translate('lier_cette_d_l_78975')}
                    </ToggleText>

                    <Spacer height="6" />
                </>
            )}

            {isToggled ? (
                <RichSelect
                    isSearchVisible={true}
                    label={translate('_quelle_d_l_ga_70215')}
                    name="delegation.parentId"
                >
                    {props.organizationDelegations.map((delegation) => (
                        <option key={delegation.id} value={delegation.id}>
                            {delegation.name}
                        </option>
                    ))}
                </RichSelect>
            ) : (
                <FormTextInput label={translate('nom_de_votre_d_69290')} name="delegation.name" />
            )}

            <Spacer height="6" />

            <RichSelect
                isCreateVisible={true}
                isSearchVisible={true}
                label={translate('cat_gorie_00291')}
                name="delegation.delegationCategoryId"
                onCreateClick={() => {
                    setIsCreateCategoryOpen(true);
                }}
            >
                {delegationsCategories.map(({ id, name }) => (
                    <option key={id} value={id}>
                        {name}
                    </option>
                ))}
            </RichSelect>

            <Spacer height="6" />

            <RichSelect
                isSearchLoading={isLoading}
                isSearchVisible={true}
                label={translate('reponsable_82458_plural')}
                multiple={true}
                name="delegation.leadersIds"
                searchElement={
                    <TextInput
                        icon="magnifying-glass"
                        placeholder={translate('rechercher_un_m_05904')}
                        state="search"
                        value={name}
                        onChange={setName}
                    />
                }
            >
                {usersInfosOptions}
            </RichSelect>

            {isCreateCategoryOpen && (
                <CreateDelegationCategory
                    eventId={eventId}
                    organizationId={organizationId}
                    onClose={() => {
                        setIsCreateCategoryOpen(false);
                    }}
                    onSuccess={(delegationCategory) => {
                        setDelegationsCategories([...delegationsCategories, delegationCategory]);

                        props.change('delegation.delegationCategoryId', delegationCategory.id);
                    }}
                />
            )}
        </>
    );
};

interface ICreateEventDelegationInformationsProps {
    categories: Array<Pick<DelegationsCategory, 'id' | 'name'>>;
    initialParentId: Emptyable<DelegationId>;
    leadersIds: UsersInfoId[];
    organizationDelegations: Array<Pick<Delegation, 'id' | 'name'>>;

    change(name: string, value: any): void;
}

export const CreateEventDelegationInformations = (
    props: ICreateEventDelegationInformationsProps
) => (
    <CreateDelegationInformations
        categories={props.categories}
        change={props.change}
        delegationType="event"
        initialParentId={props.initialParentId}
        leadersIds={props.leadersIds}
        organizationDelegations={props.organizationDelegations}
    />
);

interface ICreateOrganizationDelegationInformationsProps {
    categories: Array<Pick<DelegationsCategory, 'id' | 'name'>>;
    leadersIds: UsersInfoId[];

    change(name: string, value: any): void;
}

export const CreateOrganizationDelegationInformations = (
    props: ICreateOrganizationDelegationInformationsProps
) => (
    <CreateDelegationInformations
        categories={props.categories}
        change={props.change}
        delegationType="organization"
        initialParentId={null}
        leadersIds={props.leadersIds}
        organizationDelegations={[]}
    />
);
